/**
 * Formatting helpers.
 *
 * Money arrives from the API as a decimal string ("1234.56"), never a float -
 * the backend deliberately avoids binary floating point for currency. These
 * helpers parse that string only for display; nothing here does arithmetic on
 * money, which stays server-side where the audited calculation lives.
 *
 * Locale/currency come from config/app.config.json (see @/config) rather
 * than an inline literal, so changing the deployment default is a one-line
 * JSON edit, not a code change - see that file's own header on how it stays
 * in sync with the backend's copy of the same values.
 */
import { appConfig } from "@/config";

/**
 * A display name for an Employee that may have arrived nested inside another
 * resource (a leave request's `employee`, an attendance record's `employee`,
 * etc.) rather than fetched directly from GET /employees or /employees/:id.
 *
 * `full_name` is NOT a real column on the domain.Employee struct - it's a
 * method (`Employee.FullName()`) that only the employee handler's own
 * response DTOs call and serialize. Every other endpoint that nests an
 * Employee via a plain Go struct association (GORM Preload) marshals the
 * bare struct, which has no `full_name` key at all - so a nested employee's
 * `full_name` is `undefined` at runtime even though the TS type marks it
 * required. This falls back to first_name/last_name (always present on any
 * Employee JSON, nested or not) rather than trusting `full_name` blindly.
 */
export function employeeDisplayName(
  employee?: { full_name?: string; first_name?: string; last_name?: string } | null,
): string {
  if (employee?.full_name) return employee.full_name;
  const combined = [employee?.first_name, employee?.last_name].filter(Boolean).join(" ").trim();
  return combined || "Employee";
}

/**
 * employeeDisplayName() plus the employee's human-readable code in
 * parentheses ("Jane Doe (EMP-0002)") - a separate function rather than
 * changing employeeDisplayName()'s own output, since that helper is called
 * from tight-space contexts (avatar labels, page titles) where every
 * existing caller was written assuming a bare name; call sites that want
 * the code alongside the name opt in to this one explicitly instead. Falls
 * back to the bare name when no code is available (a User-linked context
 * with no employee_code field, or a not-yet-loaded record), never shows an
 * empty "()" pair.
 */
export function employeeDisplayNameWithCode(
  employee?: { full_name?: string; first_name?: string; last_name?: string; employee_code?: string } | null,
): string {
  const name = employeeDisplayName(employee);
  return employee?.employee_code ? `${name} (${employee.employee_code})` : name;
}

const LOCALE = appConfig.default_locale;
const CURRENCY = appConfig.default_currency;

const inr = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const inrCompact = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: CURRENCY,
  notation: "compact",
  // Explicit, not left to the runtime: ICU versions disagree on a compact
  // currency's minimum fraction digits (Node 22 gave "₹42.0K", Node 24 and
  // current browsers "₹42K"), which made the same page render differently
  // on different machines.
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Formats a decimal-string amount as "₹1,23,456.00". */
export function formatMoney(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined || amount === "") return inr.format(0);
  const value = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  if (Number.isNaN(value)) return inr.format(0);
  return inr.format(value);
}

/** Formats a decimal-string amount compactly, e.g. "₹4.3L" for a dashboard tile. */
export function formatMoneyCompact(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined || amount === "") return inrCompact.format(0);
  const value = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  if (Number.isNaN(value)) return inrCompact.format(0);
  return inrCompact.format(value);
}

/** Formats a plain number with Indian digit grouping, no currency symbol. */
export function formatNumber(value: number | string | null | undefined, decimals = 0): string {
  if (value === null || value === undefined || value === "") return "0";
  const n = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isNaN(n)) return "0";
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/** Formats "YYYY-MM-DD" as "15 Jul 2025". Passes through anything unparsable.
 * A full timestamp ("2026-10-22T00:00:00Z", how the API sends Go date
 * columns) is read by its calendar date, never shifted by the viewer's
 * timezone - appending a time to it used to make it invalid, so it showed raw. */
export function formatDate(date: string | null | undefined): string {
  if (!date) return "-";
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  const parsed = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(NaN);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(LOCALE, { day: "2-digit", month: "short", year: "numeric" });
}

/** Formats an RFC3339 timestamp as "15 Jul 2025, 09:42 AM". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Formats an RFC3339 timestamp as "2 mins ago" / "3 days ago", falling back
 * to the plain date once it's further away than a month - a relative stamp
 * that old reads as noise, not information. */
export function timeAgo(value: string | null | undefined): string {
  if (!value) return "-";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return value;
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));

  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(value.slice(0, 10));
}

/** Formats "YYYY-MM" as "July 2025". */
export function formatPeriod(period: string | null | undefined): string {
  if (!period) return "-";
  const [year, month] = period.split("-");
  if (!year || !month) return period;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(LOCALE, { month: "long", year: "numeric" });
}

/** The current calendar month as "YYYY-MM", used to default period pickers. */
export function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** The previous calendar month as "YYYY-MM" - payroll is usually run for it. */
export function previousPeriod(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Today as "YYYY-MM-DD", the wire format the API expects for dates.
 *
 * Formats the LOCAL calendar date. It must never go via toISOString(), which
 * converts to UTC first and so returns yesterday's date for the first 5.5
 * hours of every day in IST (and tomorrow's late in the evening west of
 * Greenwich) - a whole class of "the record I just created isn't there" bugs.
 * Attendance has its own canonical business-date module built on the same
 * rule; see lib/utils/attendance-date.ts. */
export function todayISO(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Renders a person's initials for an avatar fallback, e.g. "Ramesh Kumar" -> "RK". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Title-cases a snake_case status enum for display, e.g. "half_day" -> "Half Day". */
export function titleCase(value: string): string {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Reduces a raw User-Agent string to "Browser / OS" for display in login
 * activity, e.g. "Chrome / Windows". Deliberately coarse - this is a label,
 * not a device-fingerprinting library - so it only distinguishes the
 * handful of browsers/platforms an HR audience would recognise.
 */
export function parseUserAgent(userAgent: string | null | undefined): string {
  if (!userAgent) return "Unknown device";

  let browser = "Unknown browser";
  if (/Edg\//.test(userAgent)) browser = "Edge";
  else if (/OPR\//.test(userAgent)) browser = "Opera";
  else if (/Chrome\//.test(userAgent)) browser = "Chrome";
  else if (/Firefox\//.test(userAgent)) browser = "Firefox";
  else if (/Safari\//.test(userAgent) && /Version\//.test(userAgent)) browser = "Safari";
  else if (/curl\//i.test(userAgent)) browser = "curl";

  let os = "Unknown OS";
  if (/Windows/.test(userAgent)) os = "Windows";
  else if (/Mac OS X/.test(userAgent)) os = "macOS";
  else if (/Android/.test(userAgent)) os = "Android";
  else if (/iPhone|iPad|iOS/.test(userAgent)) os = "iOS";
  else if (/Linux/.test(userAgent)) os = "Linux";

  return `${browser} / ${os}`;
}

/** Formats a millisecond duration as "420ms" or "1.3s" - trace/query latency. */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/** Formats an RFC3339 timestamp as "3m ago", falling back to formatDateTime
 * once it's more than a day old - a trace/error feed reads better relative
 * to "now" than as an absolute clock time. */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return "-";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return value;
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return formatDateTime(value);
}

/** Formats an RFC3339 deadline as "in 2h 15m" (still ahead) or "3h 20m overdue"
 * (past due) - a helpdesk SLA countdown, not a generic relative-time label
 * like timeAgo (which only ever looks backward). */
export function formatDueIn(value: string | null | undefined): string {
  if (!value) return "-";
  const due = new Date(value).getTime();
  if (Number.isNaN(due)) return value;
  const diffMinutes = Math.round((due - Date.now()) / 60000);
  const overdue = diffMinutes < 0;
  const totalMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  return overdue ? `${parts} overdue` : `in ${parts}`;
}

/** Formats a byte count as "1.2 MB" etc., for a document's file_size. */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`;
}
